import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import Switch from "../Switch";
import { useAuth } from "../../providers/AuthContext";
import { getHardwareProducts, getAgreementHardwareAssortment, replaceAgreementHardwareAssortment } from "../../api/HardwareProducts";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups";

/**
 * The hardware assortment of one customer (PBI #305): a matrix of catalogue products against
 * "All billing groups" and each billing group of the agreement. Saved as a whole with its own
 * Save button; last writer wins, and the tab reloads after every save.
 */
const HardwareAssortmentTab = ({ agreementPublicId }) => {
  const { getAccessToken } = useAuth();
  const [products, setProducts] = useState([]);
  const [billingGroups, setBillingGroups] = useState([]);
  const [selection, setSelection] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const accessToken = await getAccessToken();
      const [catalogue, groups, assortment] = await Promise.all([
        getHardwareProducts(accessToken, true),
        getBillingGroupsByAgreement(agreementPublicId, accessToken),
        getAgreementHardwareAssortment(agreementPublicId, accessToken),
      ]);
      setProducts(catalogue);
      setBillingGroups(groups || []);
      setSelection(
        Object.fromEntries(
          (assortment?.products || []).map((item) => [
            item.hardwareProductPublicId,
            { agreementWide: !!item.agreementWide, billingGroupPublicIds: item.billingGroupPublicIds || [] },
          ])
        )
      );
      setHasChanges(false);
    } catch (error) {
      toast.error(error.message || "Failed to load hardware assortment");
    } finally {
      setLoading(false);
    }
  }, [agreementPublicId, getAccessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const entryFor = (productId) => selection[productId] || { agreementWide: false, billingGroupPublicIds: [] };

  const toggleAgreementWide = (productId, checked) => {
    setSelection((current) => ({ ...current, [productId]: { ...entryFor(productId), agreementWide: checked } }));
    setHasChanges(true);
  };

  const toggleBillingGroup = (productId, billingGroupPublicId, checked) => {
    setSelection((current) => {
      const entry = entryFor(productId);
      const ids = checked
        ? [...new Set([...entry.billingGroupPublicIds, billingGroupPublicId])]
        : entry.billingGroupPublicIds.filter((id) => id !== billingGroupPublicId);
      return { ...current, [productId]: { ...entry, billingGroupPublicIds: ids } };
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const accessToken = await getAccessToken();
      const payload = {
        agreementPublicId,
        products: Object.entries(selection)
          .filter(([, entry]) => entry.agreementWide || entry.billingGroupPublicIds.length > 0)
          .map(([hardwareProductPublicId, entry]) => ({
            hardwareProductPublicId,
            agreementWide: entry.agreementWide,
            billingGroupPublicIds: entry.agreementWide ? [] : entry.billingGroupPublicIds,
          })),
      };
      await replaceAgreementHardwareAssortment(agreementPublicId, payload, accessToken);
      toast.success("Hardware assortment saved", { iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" } });
      await load();
    } catch (error) {
      toast.error(error.message || "Failed to save hardware assortment");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-mute-400">Loading hardware assortment...</p>;

  return (
    <>
      <div className="flex justify-between items-center">
        <div>
          <h2>Hardware assortment</h2>
          <p className="text-sm text-mute-400">
            Products the customer may order. "All billing groups" shows a product to everyone under the agreement;
            otherwise tick the billing groups it applies to. Unpublished products stay hidden until published.
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={!hasChanges || isSaving}>Save assortment</button>
      </div>

      {products.length === 0 ? (
        <p className="text-sm">No products in the catalogue yet. Create them under Hardware catalogue first.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table-default w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">Product</th>
                <th className="text-left">Category</th>
                <th className="text-left">All billing groups</th>
                {billingGroups.map((group) => (
                  <th key={group.billingGroupPublicId} className="text-left">{group.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map((product) => {
                const entry = entryFor(product.hardwareProductPublicId);
                return (
                  <tr key={product.hardwareProductPublicId}>
                    <td>
                      {product.name}
                      {!product.isPublished && <span className="ml-2 text-xs text-mute-400">(unpublished)</span>}
                    </td>
                    <td>{product.category || ""}</td>
                    <td>
                      <Switch
                        type="checkbox"
                        checked={entry.agreementWide}
                        onChange={(checked) => toggleAgreementWide(product.hardwareProductPublicId, checked)}
                      />
                    </td>
                    {billingGroups.map((group) => (
                      <td key={group.billingGroupPublicId}>
                        <Switch
                          type="checkbox"
                          checked={entry.agreementWide || entry.billingGroupPublicIds.includes(group.billingGroupPublicId)}
                          disabled={entry.agreementWide}
                          onChange={(checked) => toggleBillingGroup(product.hardwareProductPublicId, group.billingGroupPublicId, checked)}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default HardwareAssortmentTab;
